import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Twitter, Linkedin, ShieldCheck, Radio, Cpu, Lock } from 'lucide-react';

const FooterLink = ({ label, to, internalKey, onNavigate }) => {
  const handleClick = (e) => {
    if (onNavigate && internalKey) {
      e.preventDefault();
      onNavigate(internalKey);
    }
  };

  return (
    <li>
      <Link
        to={to || '#'}
        onClick={handleClick}
        className="group flex items-center gap-2 text-slate-400 hover:text-teal-400 transition-colors text-sm text-left"
      >
        <span className="h-1 w-1 rounded-full bg-slate-700 group-hover:bg-teal-400 transition-colors" />
        {label}
      </Link>
    </li>
  );
};

const SiteFooter = ({ settings, onNavigate }) => {
  return (
    <footer className="relative overflow-hidden border-t border-white/5 bg-[#020617] mt-auto">
      <div className="absolute inset-0 pointer-events-none opacity-[0.07] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] " />
      <div className="absolute -top-32 left-10 h-64 w-64 rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 right-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="rounded-[2rem] border border-white/10 bg-white/[0.025] backdrop-blur-xl shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8 border-b border-white/10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="inline-flex items-center rounded-full border border-teal-500/25 bg-teal-500/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-teal-400">
                  Autonomous Security Infrastructure
                </div>
                <h3 className="mt-5 text-2xl md:text-2xl font-black text-white tracking-tight">
                  Built for continuous protection, recovery, and trust.
                </h3>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-400 mb-2" />
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Status</p>
                  <p className="text-sm font-bold text-emerald-400">Operational</p>
                </div>

                <div className="rounded-2xl border border-teal-500/20 bg-teal-500/10 px-4 py-3">
                  <Radio className="w-5 h-5 text-teal-400 mb-2" />
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Telemetry</p>
                  <p className="text-sm font-bold text-teal-400">Live</p>
                </div>

                <div className="rounded-2xl border border-purple-500/20 bg-purple-500/10 px-4 py-3">
                  <Cpu className="w-5 h-5 text-purple-400 mb-2" />
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Engine</p>
                  <p className="text-sm font-bold text-purple-400">Neural</p>
                </div>

                <div className="rounded-2xl border border-blue-500/20 bg-blue-500/10 px-4 py-3">
                  <Lock className="w-5 h-5 text-blue-400 mb-2" />
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Mode</p>
                  <p className="text-sm font-bold text-blue-400">Zero Trust</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12">
            <div className="col-span-1 md:col-span-1 space-y-5">
              <div className="flex items-center">
                {settings?.site_logo ? (
                  <img src={settings.site_logo} alt="Logo" className="h-9 mr-3 object-contain" />
                ) : (
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl mr-3 flex items-center justify-center font-bold text-white shadow-lg shadow-blue-500/20">
                    A
                  </div>
                )}

                <span className="text-white font-bold text-lg tracking-tight">
                  {settings?.site_title || 'AIRS'} <span className="text-teal-500">Security</span>
                </span>
              </div>

              <p className="text-slate-500 text-sm leading-relaxed">
                The world's first self-healing, autonomous cybersecurity agent designed for zero-trust infrastructure.
              </p>

              <div className="flex space-x-3 pt-2">
                <Github className="w-9 h-9 p-2 rounded-xl border border-white/10 bg-white/5 text-slate-500 hover:text-white hover:bg-white/10 cursor-pointer transition-all" />
                <Twitter className="w-9 h-9 p-2 rounded-xl border border-white/10 bg-white/5 text-slate-500 hover:text-teal-400 hover:bg-white/10 cursor-pointer transition-all" />
                <Linkedin className="w-9 h-9 p-2 rounded-xl border border-white/10 bg-white/5 text-slate-500 hover:text-teal-500 hover:bg-white/10 cursor-pointer transition-all" />
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-teal-400" /> Product
              </h4>
              <ul className="space-y-3">
                <FooterLink label="Features" to="/features" internalKey="product" onNavigate={onNavigate} />
                <FooterLink label="Pricing" to="/pricing" internalKey="pricing" onNavigate={onNavigate} />
                <FooterLink label="Download Agent" to="/download" internalKey="download" onNavigate={onNavigate} />
                <FooterLink label="API Documentation" to="/docs" internalKey="docs" onNavigate={onNavigate} />
                <FooterLink label="Changelog" to="/changelog" internalKey="changelog" onNavigate={onNavigate} />
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-purple-400" /> Company
              </h4>
              <ul className="space-y-3">
                <FooterLink label="About Us" to="/pages/about-us" internalKey="about-us" onNavigate={onNavigate} />
                <FooterLink label="Careers" to="/pages/careers" internalKey="careers" onNavigate={onNavigate} />
                <FooterLink label="Blog" to="/blog" internalKey="blog" onNavigate={onNavigate} />
                <FooterLink label="Contact Support" to="/contact" internalKey="contact" onNavigate={onNavigate} />
                <FooterLink label="Partners" to="/pages/partners" internalKey="partners" onNavigate={onNavigate} />
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-400" /> Legal
              </h4>
              <ul className="space-y-3">
                <FooterLink label="Privacy Policy" to="/pages/privacy-policy" internalKey="privacy-policy" onNavigate={onNavigate} />
                <FooterLink label="Terms & Conditions" to="/pages/terms-conditions" internalKey="terms-conditions" onNavigate={onNavigate} />
                <FooterLink label="Cookie Policy" to="/pages/cookie-policy" internalKey="cookie-policy" onNavigate={onNavigate} />
                <FooterLink label="GDPR Compliance" to="/pages/gdpr-compliance" internalKey="gdpr-compliance" onNavigate={onNavigate} />
                <FooterLink label="Refund Policy" to="/pages/refund-policy" internalKey="refund-policy" onNavigate={onNavigate} />
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-slate-600 gap-4">
          <p>&copy; {new Date().getFullYear()} {settings?.site_title || 'AIRS Security'}. All rights reserved.</p>

          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            <span>
              System Status: <span className="text-emerald-500">● Operational</span>
            </span>
            <span>{settings?.current_version || 'v15.0.0'} (Neural-Net)</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;